'use client';
import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const navGroups = [
  {
    label: 'Operations',
    items: [
      { href: '/mission-operations-center', icon: 'SignalIcon', label: 'Mission Ops', badge: 2 },
      { href: '/arctic-research-dashboard', icon: 'GlobeAltIcon', label: 'Arctic Research', badge: 0 },
    ],
  },
  {
    label: 'Family',
    items: [
      { href: '/parent-teacher-dashboard', icon: 'UserGroupIcon', label: 'Parent Dashboard', badge: 0 },
      { href: '/kid-ocean-explorer', icon: 'StarIcon', label: 'Ocean Explorer', badge: 0 },
      { href: '/ocean-guardian-mode', icon: 'ShieldCheckIcon', label: 'Guardian Mode', badge: 0 },
    ],
  },
];

export default function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const pathname = usePathname();

  return (
    <aside
      className="flex flex-col h-full bg-card border-r border-border transition-all duration-300 ease-in-out flex-shrink-0 z-20"
      style={{ width: collapsed ? 64 : 240 }}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 py-5 border-b border-border min-h-[72px]">
        <AppLogo size={32} />
        {!collapsed && (
          <span className="font-semibold text-sm text-foreground tracking-tight truncate">
            OceanAcousticOS
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-ocean">
        {navGroups.map((group) => (
          <div key={`group-${group.label}`} className="mb-6">
            {!collapsed && (
              <p className="px-4 mb-2 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                {group.label}
              </p>
            )}
            <ul className="space-y-1 px-2">
              {group.items.map((item) => {
                const isActive = pathname === item.href;
                return (
                  <li key={`nav-${item.href}`}>
                    <Link
                      href={item.href}
                      title={collapsed ? item.label : undefined}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative
                        ${isActive
                          ? 'bg-primary/10 text-primary border border-primary/20' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                        }`}
                    >
                      <Icon
                        name={item.icon as Parameters<typeof Icon>[0]['name']}
                        size={18}
                        variant={isActive ? 'solid' : 'outline'}
                        className={isActive ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}
                      />
                      {!collapsed && (
                        <>
                          <span className="flex-1 truncate">{item.label}</span>
                          {item.badge > 0 && (
                            <span className="ml-auto bg-negative text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                      {collapsed && item.badge > 0 && (
                        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-negative" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>

      {/* Bottom */}
      <div className="border-t border-border p-2">
        <button
          onClick={onToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-all duration-150 text-sm"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Icon
            name={collapsed ? 'ChevronRightIcon' : 'ChevronLeftIcon'}
            size={16}
          />
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
        {!collapsed && (
          <div className="mt-2 px-3 py-2 rounded-lg bg-muted/50 flex items-center gap-3">
            <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Icon name="UserCircleIcon" size={16} className="text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-foreground truncate">Dr. Amara Osei</p>
              <p className="text-[10px] text-muted-foreground truncate">Mission Operator</p>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}